/**
 * Explicit YouTube disconnect and Rhythmé account-data deletion.
 *
 * Only these routines delete provider library data. Browser close, Firebase
 * sign-out, missing OAuth, and expired access tokens must not call them.
 *
 * Firestore has no cascade delete: child collections are removed before
 * parents, in bounded batches.
 */
import { collection, getDocs } from 'firebase/firestore';
import { PLAYLIST_KINDS } from '../catalog/schema';
import { db } from '../firebase';
import {
  isNativePlaylistId,
  isPhase4PlaylistDoc,
  rawYouTubePlaylistId,
} from './playlistIdMigration';
import { commitUserLibraryDeletes } from './userLibrary';
import { clearYouTubeAuthorization, revokeYouTubeAccess } from './youtubeAuth';

const YOUTUBE = 'youtube';

function nonemptyString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function emptyCounts() {
  return {
    playlistDocsDeleted: 0,
    itemDocsDeleted: 0,
    syncRunDocsDeleted: 0,
    assetDocsDeleted: 0,
    legacyBareDocsDeleted: 0,
    nativePlaylistsRetained: 0,
    sessionDocsDeleted: 0,
    enrichmentRequestDocsDeleted: 0,
    profileDeleted: false,
    batchesUsed: 0,
  };
}

async function listImmediateDocs(firestore, segments) {
  const snapshot = await getDocs(collection(firestore, ...segments));
  return (snapshot?.docs || []).map((entry) => ({
    id: entry.id,
    data: entry.data() || {},
  }));
}

async function deleteSegmentRows(uid, rows, options) {
  if (!rows.length) return 0;
  const persistDeletes = options.commitUserLibraryDeletes || commitUserLibraryDeletes;
  return persistDeletes(rows, { firestore: options.firestore || db });
}

function deleteRow(uid, segments) {
  return { uid, segments };
}

/**
 * PR12 v0 leftover: bare doc id, no catalogSchemaVersion, explicit YouTube
 * source/provider, and a matching raw playlist id when one is present.
 * Does not guess from a "PL" prefix.
 */
export function isLegacyBareYouTubePlaylistDoc(id, data = {}) {
  if (!nonemptyString(id) || isNativePlaylistId(id) || data.kind === PLAYLIST_KINDS.RHYTHME_NATIVE) {
    return false;
  }
  if (isPhase4PlaylistDoc(data) || id.includes(':')) return false;
  const source = nonemptyString(data.source) || nonemptyString(data.provider);
  if (source !== YOUTUBE) return false;
  const raw = nonemptyString(data.playlistId) || nonemptyString(data.providerPlaylistId);
  if (raw && raw !== id && raw !== rawYouTubePlaylistId(id, data)) {
    return false;
  }
  return true;
}

export function isYouTubeAuthorizedPlaylistDoc(id, data = {}) {
  if (isNativePlaylistId(id) || data.kind === PLAYLIST_KINDS.RHYTHME_NATIVE) {
    return false;
  }
  if (isLegacyBareYouTubePlaylistDoc(id, data)) return true;
  if (!isPhase4PlaylistDoc(data)) return false;
  const provider = nonemptyString(data.provider) || nonemptyString(data.source);
  if (provider === YOUTUBE) return true;
  return typeof id === 'string' && id.startsWith('yt:');
}

export function isYouTubeAuthorizedAssetDoc(id, data = {}) {
  if (typeof id === 'string' && id.startsWith('yt:')) return true;
  return nonemptyString(data.provider) === YOUTUBE;
}

async function deletePlaylistSubtree(uid, playlistId, options, counts, { countAsLegacy = false } = {}) {
  const firestore = options.firestore || db;
  const items = await listImmediateDocs(firestore, [
    'users', uid, 'playlists', playlistId, 'items',
  ]);
  const syncRuns = await listImmediateDocs(firestore, [
    'users', uid, 'playlists', playlistId, 'syncRuns',
  ]);

  const childDeletes = [
    ...items.map((item) => deleteRow(uid, ['playlists', playlistId, 'items', item.id])),
    ...syncRuns.map((run) => deleteRow(uid, ['playlists', playlistId, 'syncRuns', run.id])),
  ];
  counts.batchesUsed += await deleteSegmentRows(uid, childDeletes, options);
  counts.itemDocsDeleted += items.length;
  counts.syncRunDocsDeleted += syncRuns.length;

  counts.batchesUsed += await deleteSegmentRows(
    uid,
    [deleteRow(uid, ['playlists', playlistId])],
    options,
  );
  if (countAsLegacy) counts.legacyBareDocsDeleted += 1;
  else counts.playlistDocsDeleted += 1;
}

export async function deleteYouTubeAuthorizedLibrary(uid, options = {}) {
  if (!uid) {
    return emptyCounts();
  }
  const firestore = options.firestore || db;
  const counts = emptyCounts();
  const playlists = await listImmediateDocs(firestore, ['users', uid, 'playlists']);

  for (const playlist of playlists) {
    if (isNativePlaylistId(playlist.id) || playlist.data.kind === PLAYLIST_KINDS.RHYTHME_NATIVE) {
      counts.nativePlaylistsRetained += 1;
      continue;
    }
    if (!isYouTubeAuthorizedPlaylistDoc(playlist.id, playlist.data)) {
      counts.nativePlaylistsRetained += 1;
      continue;
    }
    const legacy = isLegacyBareYouTubePlaylistDoc(playlist.id, playlist.data);
    // eslint-disable-next-line no-await-in-loop
    await deletePlaylistSubtree(uid, playlist.id, options, counts, {
      countAsLegacy: legacy,
    });
  }

  const assets = await listImmediateDocs(firestore, ['users', uid, 'assets']);
  const youtubeAssets = assets.filter((asset) => (
    isYouTubeAuthorizedAssetDoc(asset.id, asset.data)
  ));
  counts.batchesUsed += await deleteSegmentRows(
    uid,
    youtubeAssets.map((asset) => deleteRow(uid, ['assets', asset.id])),
    options,
  );
  counts.assetDocsDeleted += youtubeAssets.length;
  return counts;
}

/**
 * Explicit YouTube disconnect. Keeps Firebase/Rhythmé auth and events.
 */
export async function disconnectYouTube(uid, options = {}) {
  const revoke = options.revokeYouTubeAccess || revokeYouTubeAccess;
  let revokeResult = { ok: true, revoked: false, reason: 'skipped' };
  try {
    revokeResult = await revoke({
      fetchImpl: options.fetchImpl,
    });
  } catch (error) {
    revokeResult = { ok: false, revoked: false, reason: 'revoke-failed', error };
  }

  clearYouTubeAuthorization();

  const deletion = await deleteYouTubeAuthorizedLibrary(uid, options);
  return {
    ...deletion,
    revoke: revokeResult,
    firebaseAuthPreserved: true,
  };
}

/**
 * Firestore user-subtree cleanup for account deletion.
 *
 * Deletes children first, then the users/{uid} profile/root last.
 * Does not delete Firebase Auth users. Does not erase events/** — that
 * remains the architecture's unresolved Phase-3 policy gap.
 *
 * If any child deletion fails, the root is left in place and the result
 * is not reported as complete.
 */
export async function deleteAccountUserData(uid, options = {}) {
  if (!uid) {
    return {
      ...emptyCounts(),
      complete: false,
      userRootDeleted: false,
      authAccountDeleted: false,
      eventsErasure: 'unresolved_phase3_policy_gap',
    };
  }

  const firestore = options.firestore || db;
  const counts = emptyCounts();

  try {
    const sessions = await listImmediateDocs(firestore, ['users', uid, 'sessions']);
    counts.batchesUsed += await deleteSegmentRows(
      uid,
      sessions.map((session) => deleteRow(uid, ['sessions', session.id])),
      options,
    );
    counts.sessionDocsDeleted += sessions.length;

    const playlists = await listImmediateDocs(firestore, ['users', uid, 'playlists']);
    for (const playlist of playlists) {
      // eslint-disable-next-line no-await-in-loop
      await deletePlaylistSubtree(uid, playlist.id, options, counts);
    }

    const assets = await listImmediateDocs(firestore, ['users', uid, 'assets']);
    counts.batchesUsed += await deleteSegmentRows(
      uid,
      assets.map((asset) => deleteRow(uid, ['assets', asset.id])),
      options,
    );
    counts.assetDocsDeleted += assets.length;

    const requests = await listImmediateDocs(
      firestore,
      ['users', uid, 'enrichmentRequests'],
    );
    counts.batchesUsed += await deleteSegmentRows(
      uid,
      requests.map((request) => deleteRow(uid, ['enrichmentRequests', request.id])),
      options,
    );
    counts.enrichmentRequestDocsDeleted += requests.length;
  } catch (error) {
    return {
      ...counts,
      complete: false,
      profileDeleted: false,
      userRootDeleted: false,
      authAccountDeleted: false,
      childDeletionFailed: true,
      childDeletionError: error?.code || error?.message || 'child-delete-failed',
      eventsErasure: 'unresolved_phase3_policy_gap',
      nativePlaylistsRetained: 0,
    };
  }

  counts.batchesUsed += await deleteSegmentRows(
    uid,
    [deleteRow(uid, [])],
    options,
  );
  counts.profileDeleted = true;
  counts.userRootDeleted = true;

  return {
    ...counts,
    complete: true,
    authAccountDeleted: false,
    eventsErasure: 'unresolved_phase3_policy_gap',
    nativePlaylistsRetained: 0,
  };
}
